/**
 * Roadside breakdown costs — TRBUD-409.
 *
 * A roadside job is a work order with `work_order.is_roadside` set: ticked at
 * creation or conversion ("Roadside breakdown"), or corrected later with
 * `PUT /work-orders/:id/roadside`. Eden's rulings, 22 October 2026:
 *
 * - The cost gets its own panel on Reports ("that's an important stat"), not
 *   only saved reports.
 * - The box starts ticked when the unit has an open "Roadside failure"
 *   downtime, and stays editable.
 * - No automatic backfill: old jobs are ticked by hand.
 *
 * Recorded money only: the panel reads the same live cost lines as the spend
 * headline (`v_cost_line_spend`), so a voided line is absent, a reversal nets
 * and a vendor credit application (which writes no cost line) changes nothing.
 * Indirect cost (downtime, lost loads) is out of scope.
 *
 * Browser-safe: zod, types and labels.
 */
import { z } from "zod";
import { idempotencyKey, reportsQuery, strictObject } from "./requests.js";
import type { ReportJobFilter } from "./category-period.js";
import type { CostStatus, DowntimeReason } from "./vocabulary.js";

/** The downtime reason that pre-ticks "Roadside breakdown" (Eden's ruling 61). */
export const ROADSIDE_DOWNTIME_REASON = "roadside_failure" satisfies DowntimeReason;

/**
 * Where the Roadside breakdown box starts: ticked when the unit has an OPEN
 * downtime for a roadside failure, unticked otherwise. Only the starting
 * value; the person can change it either way. `openDowntimeReason` is
 * `AssetRecord.downtime.reason`, null when the unit is not down.
 */
export function roadsideStartsTicked(openDowntimeReason: string | null | undefined): boolean {
  return openDowntimeReason === ROADSIDE_DOWNTIME_REASON;
}

/** The tag on the job header and queue row. Plain: never red or orange. */
export const ROADSIDE_TAG = "Roadside";
export const ROADSIDE_TAG_TITLE = "Marked as a roadside breakdown. Its recorded costs count in Reports, Roadside breakdowns.";

/** `PUT /work-orders/:id/roadside`. A ticket's change copies to its packages. */
export const setWorkOrderRoadsideBody = strictObject({
  isRoadside: z.boolean(),
  idempotencyKey,
});
export type SetWorkOrderRoadsideBody = z.infer<typeof setWorkOrderRoadsideBody>;

/** What `PUT /work-orders/:id/roadside` answers. */
export interface SetWorkOrderRoadsideReceipt {
  isRoadside: boolean;
  /** Packages of an itemized ticket that took the same value. 0 on a plain job. */
  packagesUpdated: number;
}

/** `v_cost_line_spend.spend_class`. */
export const roadsideSpendClasses = ["labour", "parts", "other"] as const;
export type RoadsideSpendClass = (typeof roadsideSpendClasses)[number];
/** `v_cost_line_spend.spend_side`. */
export const roadsideSpendSides = ["vendor", "in_house", "maintainx_unsplit"] as const;
export type RoadsideSpendSide = (typeof roadsideSpendSides)[number];
/**
 * A supplier is the vendor that billed the line (the biller keeps the spend,
 * TRBUD-412), else one of these three keys for a line with no vendor.
 */
export const roadsideNoVendorSuppliers = ["in_house", "maintainx_unsplit", "none"] as const;
export type RoadsideNoVendorSupplier = (typeof roadsideNoVendorSuppliers)[number];

export const ROADSIDE_SPEND_CLASS_LABEL: Record<RoadsideSpendClass, string> = {
  labour: "Labour",
  parts: "Parts",
  other: "Other (tow, service call, fees, tax)",
};
export const ROADSIDE_SIDE_LABEL: Record<RoadsideSpendSide, string> = {
  vendor: "Vendor",
  in_house: "In-house",
  maintainx_unsplit: "MaintainX, not split",
};
/** The supplier name a line with no vendor reads under. The SQL uses the same words. */
export const ROADSIDE_NO_VENDOR_LABEL: Record<RoadsideNoVendorSupplier, string> = {
  in_house: "In-house (no supplier)",
  maintainx_unsplit: "MaintainX, not split to a vendor",
  none: "No supplier recorded",
};

/** A job's cost status in the panel's tables and CSV. */
export const ROADSIDE_COST_STATUS_LABEL: Record<CostStatus, string> = {
  complete: "Final cost in",
  awaiting_final_cost: "Awaiting final cost",
};

/** The panel's read: the page dates and filters, plus at most one drill. */
export const roadsideCostsQuery = reportsQuery.safeExtend({
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/).refine((value) => !value.startsWith("0000-"), "Choose a valid calendar month.").optional(),
  spendClass: z.enum(roadsideSpendClasses).optional(),
  side: z.enum(roadsideSpendSides).optional(),
  supplier: z.union([z.uuid(), z.enum(roadsideNoVendorSuppliers)]).optional(),
  /** One job's lines within the selection: "a job expands to its lines". */
  workOrderId: z.uuid().optional(),
  offset: z.coerce.number().int().min(0).max(1_000_000).default(0),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});
export type RoadsideCostsQuery = z.infer<typeof roadsideCostsQuery>;

/** What a drill picked. Absent keys are not part of it. */
export interface RoadsideCostSelection {
  month?: string;
  spendClass?: RoadsideSpendClass;
  side?: RoadsideSpendSide;
  supplier?: string;
}

/** A roadside job, as the drill and the "no cost yet" list show it. */
export interface RoadsideJobRow {
  workOrderId: string;
  workOrderNumber: string;
  title: string | null;
  unitNumber: string | null;
  /** The day the job was opened, in the fleet timezone. */
  openedOn: string;
  costStatus: CostStatus;
  currency: string;
  /** The job's recorded cost inside the selection, never its lifetime total. */
  amount: string;
  costLines: number;
}

export interface RoadsideNoCostJob {
  workOrderId: string;
  workOrderNumber: string;
  title: string | null;
  unitNumber: string | null;
  openedOn: string;
  costStatus: CostStatus;
}

/** One ledger line (or one gold-view share of one, for a split MaintainX expense). */
export interface RoadsideCostLine {
  id: string;
  workOrderId: string;
  workOrderNumber: string;
  title: string | null;
  date: string;
  currency: string;
  amount: string;
  source: string;
  spendClass: RoadsideSpendClass;
  side: RoadsideSpendSide;
  supplierName: string;
}

export interface RoadsideCosts {
  filters: ReportJobFilter;
  start: string | null;
  end: string;
  /**
   * Per currency: recorded roadside cost, roadside jobs and cost lines, and
   * the share of ALL spend in the same dates and filters. `sharePercent` is
   * null when there is no spend at all to divide by.
   */
  totals: Array<{ currency: string; amount: string; jobs: number; costLines: number; allSpend: string; sharePercent: string | null }>;
  /**
   * Roadside jobs in these dates raised in TruckBuddy rather than imported
   * from MaintainX. Zero is the cold-start state: until people tick the box,
   * the panel shows MaintainX history only, and its note says so.
   */
  truckbuddyJobs: number;
  months: Array<{ month: string; currency: string; amount: string; jobs: number }>;
  classes: Array<{ spendClass: RoadsideSpendClass; side: RoadsideSpendSide; currency: string; amount: string; jobs: number }>;
  /** Largest first, at most 25. `workDoneBy` names a different shop recorded on the bill (TRBUD-412). */
  suppliers: Array<{ supplier: string; supplierName: string; currency: string; amount: string; jobs: number; workDoneBy: string[] }>;
  suppliersTruncated: boolean;
  /** Roadside jobs opened in these dates with no live cost line, oldest first, at most 50. */
  noCostYet: RoadsideNoCostJob[];
  noCostYetMore: boolean;
  selection: RoadsideCostSelection | null;
  /** The jobs behind the selection, paged. Empty without a selection. */
  jobs: RoadsideJobRow[];
  /** One job's lines (`workOrderId`), or every line on export. */
  lines: RoadsideCostLine[];
  hasMore: boolean;
}
